"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as SheetPrimitive } from "radix-ui"

import { PopoverInsideModalContext } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

function Sheet({ modal = false, ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" modal={modal} {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

/**
 * Where a sheet slides in from. "start" and "end" follow the text direction
 * (the default "end" is the right in English and the left in Arabic, RTL-05);
 * "left" and "right" are the physical edges in every language.
 */
type SheetSide = "top" | "right" | "bottom" | "left" | "start" | "end"

function SheetContent({
  className,
  children,
  side = "end",
  showCloseButton = true,
  // #300: neither Radix nor this wrapper emitted `aria-modal`, on every
  // sheet in the app. Default false — matches `Sheet`'s own `modal = false`
  // default a few lines up: MOST sheets in this codebase are deliberately
  // non-modal (background content stays operable), so `aria-modal="true"`
  // would be inaccurate for them. A caller opting into a modal sheet passes
  // `modal` here (or the existing `aria-modal="true"` override some call
  // sites already use, e.g. edit-recipient-sidepanel.tsx's "Parity with the
  // read-only drawer" comment) — `{...props}` spreads AFTER this default, so
  // either wins.
  modal = false,
  onInteractOutside,
  onFocusOutside,
  forceMount,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: SheetSide
  showCloseButton?: boolean
  modal?: boolean
}) {
  // The close button's name, in the interface language (English outside the provider).
  const { t } = useShellTranslation()
  // #389/#390 — this Sheet is non-modal (`modal = false` above), so Radix
  // renders no overlay, and the content animates out over 300ms while Presence
  // keeps it mounted at full layout and opacity. For that whole window an
  // invisible drawer still eats clicks and still holds its controls in the tab
  // order: press Escape, click the thing behind it, nothing happens.
  //
  // `inert` is the single attribute that removes a subtree from BOTH hit
  // testing and sequential focus navigation, so it closes the click half
  // (#389) and the Tab half (#390) together. A CSS-only `pointer-events-none`
  // would fix the click and leave the focus trap.
  //
  // Driven off Radix's own `data-state` via MutationObserver rather than a
  // prop, because the open state lives in Radix's context and this wrapper
  // never sees it.
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const el = contentRef.current
    if (!el) return
    const sync = () => {
      if (el.getAttribute("data-state") === "closed") {
        el.setAttribute("inert", "")
      } else {
        el.removeAttribute("inert")
      }
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(el, { attributes: true, attributeFilter: ["data-state"] })
    return () => observer.disconnect()
    // Deliberately NOT mount-scoped, and both halves earn their place:
    //
    //   sync() on every render covers CONTROLLED sheets — the caller owns
    //   `open`, so flipping it re-renders this component and we see the new
    //   data-state immediately, without waiting on a microtask.
    //
    //   the observer covers UNCONTROLLED sheets (SheetTrigger + Radix's own
    //   state), where Radix re-renders its internals but this wrapper may not
    //   re-render at all, so a render-time sync would never fire.
    //
    // Dropping to `[]` makes the controlled path depend solely on the observer
    // and the spec fails. There is no missed-mutation gap from re-running:
    // teardown and setup are synchronous within one effect pass, and setup
    // calls sync() immediately, so any flip during the swap is picked up.
  })

  return (
    <SheetPortal forceMount={forceMount}>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={contentRef}
        forceMount={forceMount}
        data-slot="sheet-content"
        data-side={side}
        aria-modal={modal ? "true" : "false"}
        onFocusOutside={(e) => {
          // Prevent sheet from closing when focus moves outside (e.g. dropdown menu
          // close restoring focus to trigger). Users can still close via X, Escape, or overlay.
          e.preventDefault()
          onFocusOutside?.(e)
        }}
        onInteractOutside={(e) => {
          // Don't close sheet when interacting with floating UI (chat, feedback widget)
          const target = e.target as HTMLElement | null
          if (target?.closest?.('[data-slot="chat-companion"], [data-slot="feedback-widget"]')) {
            e.preventDefault()
            return
          }
          onInteractOutside?.(e)
        }}
        className={cn(
          "fixed z-50 flex flex-col gap-4 bg-background px-6 shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500",
          side === "end" &&
            "inset-y-0 end-0 h-full w-3/4 border-s data-[state=closed]:slide-out-to-end data-[state=open]:slide-in-from-end sm:max-w-sm",
          side === "start" &&
            "inset-y-0 start-0 h-full w-3/4 border-e data-[state=closed]:slide-out-to-start data-[state=open]:slide-in-from-start sm:max-w-sm",
          // Physical on purpose: these name an edge, not a reading direction.
          side === "right" &&
            // rtl-codemod-ignore-next-line
            "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
          side === "left" &&
            // rtl-codemod-ignore-next-line
            "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
          side === "top" &&
            "inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className
        )}
        {...props}
      >
        <PopoverInsideModalContext.Provider value={modal}>{children}</PopoverInsideModalContext.Provider>
        {showCloseButton && (
          <SheetPrimitive.Close className="absolute top-4 end-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary">
            <XIcon className="size-4" />
            <span className="sr-only">{t("common.close")}</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 py-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 py-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
